const IMPORT_SOURCE_PROPERTY_KEY = 'import-source';
const IMPORT_DATE_PROPERTY_KEY = 'imported-page-modified-date';
export const FEEDBACK_THRESHOLD_DAYS = 90;
export const IMPORT_DATE_BUFFER_TIME = 2 * 1000 * 60; // 2 minutes

type ContentProperty = {
	key?: string | null;
	value?: string | null;
};

type ContentNodeProperties = {
	properties?: {
		nodes?: Array<ContentProperty | null | undefined> | null;
	} | null;
};

type ContentNode = ContentNodeProperties & {
	metadata?: {
		lastModifiedDate?: string | null;
	};
};

type ContentQueryData<T> = {
	content?: {
		nodes?: Array<T | null | undefined> | null;
	} | null;
};

export function getImportSource<T extends ContentNodeProperties>(
	contentQueryData: ContentQueryData<T> | undefined,
): string | undefined {
	const pageProperties = contentQueryData?.content?.nodes?.[0]?.properties?.nodes;
	const importedFromProperty = pageProperties?.find(
		(property) => property?.key === IMPORT_SOURCE_PROPERTY_KEY,
	);

	return importedFromProperty?.value || undefined;
}

export function getImportDate<T extends ContentNodeProperties>(
	contentQueryData: ContentQueryData<T> | undefined,
): Date | undefined {
	const pageProperties = contentQueryData?.content?.nodes?.[0]?.properties?.nodes;

	const originalImportDate = pageProperties
		?.find((property) => property?.key === IMPORT_DATE_PROPERTY_KEY)
		?.value?.replace(/^"|"$/g, '');

	return originalImportDate ? new Date(originalImportDate) : undefined;
}

export function isImportBannerEnabled<T extends ContentNode>(
	contentQueryData: ContentQueryData<T> | undefined,
): boolean {
	const importedFromProperty = getImportSource(contentQueryData);

	const originalImportDate = getImportDate(contentQueryData);

	const lastModifiedDate = contentQueryData?.content?.nodes?.[0]?.metadata?.lastModifiedDate
		? new Date(contentQueryData?.content?.nodes?.[0]?.metadata?.lastModifiedDate)
		: undefined;

	if (!originalImportDate || !lastModifiedDate) {
		return false;
	}

	return (
		!!importedFromProperty &&
		isPageWithinFeedbackTimeThreshold(originalImportDate) &&
		!isPageLastModifiedAfterImportDate(lastModifiedDate, originalImportDate)
	);
}

function isPageWithinFeedbackTimeThreshold(importDate: Date): boolean {
	return Date.now() <= importDate.getTime() + FEEDBACK_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
}

function isPageLastModifiedAfterImportDate(lastModifiedDate: Date, importDate: Date): boolean {
	return lastModifiedDate.getTime() > importDate.getTime() + IMPORT_DATE_BUFFER_TIME;
}
